import React, { useCallback, useMemo } from "react";
import ScreenCard from "../ScreenCard";
import { CreateMissionScreensContext } from "../../contexts/CreateMissionManager";
import { useI18n } from "../../hooks/i18n-hooks";
import MissionDeductions, {
    MissionDeductionResult,
    validateDeductionData,
} from "../../components/editors/MissionDeductions";
import { newMissionState } from "../../atoms/core";
import { useRecoilState } from "recoil";
import { MissionDeduction } from "../../__generated__/graphql";
import { useBubbleStepOrigin } from "../../hooks/use-bubble-step-origin";
import { Bubble } from "../../components/Bubble";

const CreateMissionDeductions = () => {
    const i18n = useI18n();
    const { validateScreen } = React.useContext(CreateMissionScreensContext);
    const [mission, setMission] = useRecoilState(newMissionState);

    const setDeductions = useCallback(
        (deductions: Array<MissionDeductionResult>) => {
            setMission((oldMission) => {
                return {
                    ...oldMission,
                    currentContract: {
                        ...oldMission.currentContract,
                        deductions,
                    },
                };
            });
        },
        [setMission],
    );

    const deductions = useMemo(() => {
        return (mission.currentContract?.deductions as Array<MissionDeduction>) || [];
    }, [mission.currentContract?.deductions]);

    const isValid = useMemo(() => {
        return deductions.reduce((acc, deduction) => {
            if (!acc) {
                return false;
            }
            return validateDeductionData(deduction);
        }, true);
    }, [deductions]);

    const validate = React.useCallback(() => {
        void validateScreen("CreateMissionDeductions");
    }, [validateScreen]);

    const origin = useBubbleStepOrigin("CreateMissionDeductions");

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("screens.user.create_mission_deductions.next_title"),
                    onPress: validate,
                    isValid: isValid,
                },
            ]}
        >
            <Bubble
                title={i18n.t("screens.user.create_mission_deductions.title")}
                origin={origin}
                bubbleStyle={{ paddingBottom: 0 }}
            >
                <MissionDeductions
                    deductions={deductions}
                    setDeductions={setDeductions}
                    repetition={mission.currentContract?.workingTimeClassicSpec?.repetition}
                />
            </Bubble>
        </ScreenCard>
    );
};

export default CreateMissionDeductions;
